import * as React from "react";
import { styled } from "@mui/system";
import { Modal as BaseModal } from "@mui/base/Modal";

interface GenericModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAccept?: () => void;
  onDecline?: () => void;
  children: React.ReactNode;
  acceptText?: string;
  cancelText?: string;
  header?: string;
  customCss?: React.CSSProperties;
  size?: "sm" | "md" | "lg" | "xl" | "full";
  customWidth?: string;
  custemHeight?: string;
  disableContentScroll?: boolean;
  hideCloseButton?: boolean;
}

const GenericModal = ({
  isOpen,
  onClose,
  onAccept,
  onDecline,
  children,
  acceptText = "Accept",
  cancelText = "Cancel",
  header,
  customCss,
  size = "md",
  customWidth,
  custemHeight = "auto",
  disableContentScroll = false,
  hideCloseButton = false,
}: GenericModalProps) => {
  const sizeStyles: Record<string, string> = {
    sm: "300px",
    md: "500px",
    lg: "700px",
    xl: "900px",
    full: "95vw",
  };

  return (
    <StyledModal
      open={isOpen}
      onClose={onClose}
      slots={{ backdrop: StyledBackdrop }}
    >
      <div
        className="bg-white dark:bg-gray-800  text-slate-900 dark:text-slate-100 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-2xl p-5 font-sans flex flex-col max-h-[90vh] !overflow-hidden relative"
        style={{
          width: customWidth || sizeStyles[size],
          height: custemHeight || "auto",
          ...customCss,
          zIndex: 1301,
        }}
      >
        {/* Top Bar: Always renders to hold the close button. Adjusts alignment if header is missing */}
        <div className={`flex flex-row ${header ? 'justify-between' : 'justify-end'} items-center mb-4 shrink-0`}>
          {header && (
            <h2 className="text-xl font-bold tracking-tight text-slate-900 dark:text-white">
              {header}
            </h2>
          )}
         {!hideCloseButton && (
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors text-2xl font-light leading-none"
              aria-label="Close modal"
            >
              &times;
            </button>
          )}
        </div>

        <div
          className={`flex-grow ${disableContentScroll
              ? "overflow-hidden"
              : "overflow-y-auto pr-2 -mr-2"
            }`}
        >
          {children}
        </div>

        {(onAccept || onDecline) && (
          <div className="flex justify-end gap-3 mt-4 pt-4 border-t border-slate-100 dark:border-slate-800 shrink-0">
            {onDecline && (
              <button
                onClick={onDecline}
                className="ms-3 text-gray-500 bg-white hover:bg-gray-100 focus:ring-4 focus:outline-none focus:ring-blue-300 rounded-lg border border-gray-200 text-sm font-medium px-5 py-2.5 hover:text-gray-900"
              >
                {cancelText}
              </button>
            )}
            {onAccept && (
              <button
                onClick={onAccept}
                className="px-4 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-xl transition-all shadow-lg shadow-blue-500/20"
              >
                {acceptText}
              </button>
            )}
          </div>
        )}
      </div>
    </StyledModal>
  );
};

export default GenericModal;

const StyledModal = styled(BaseModal)`
  position: fixed;
  z-index: 1300;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  overflow: hidden !important;
`;

const StyledBackdrop = styled("div")`
  z-index: -1;
  position: fixed;
  inset: 0;
  background-color: rgba(15, 23, 42, 0.7);
  -webkit-tap-highlight-color: transparent;
`;